import type { MarketAnalysisComparable, MarketComparableOrigin } from "./types";
import { MARKET_COMPARABLE_MAX, qualifyMarketComparable } from "./selectionPolicy.ts";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const inferComparableOrigin = (
  value: unknown,
  sourceSnapshot?: Record<string, unknown>
): MarketComparableOrigin => {
  if (value === "manual" || value === "reality_radar" || value === "live_provider") return value;
  if (isRecord(sourceSnapshot?.realityRadar)) return "reality_radar";
  if (typeof sourceSnapshot?.providerPropertyId === "string") return "live_provider";
  return "manual";
};

export const isAutomaticComparable = (comparable: Pick<MarketAnalysisComparable, "origin">) =>
  comparable.origin !== "manual";

export const activeMarketComparables = (comparables: MarketAnalysisComparable[]) => [
  ...comparables
    .filter(isAutomaticComparable)
    .filter((comparable) => comparable.marketRole !== "competition")
    .filter((comparable) => qualifyMarketComparable(comparable.scoreBreakdown).qualified)
    .slice(0, MARKET_COMPARABLE_MAX),
  ...comparables.filter((comparable) => !isAutomaticComparable(comparable) && comparable.marketRole !== "competition")
];

export const activeMarketCompetitors = (comparables: MarketAnalysisComparable[]) => comparables
  .filter(isAutomaticComparable)
  .filter((comparable) => comparable.marketRole === "competition")
  .filter((comparable) => comparable.decision !== "excluded");

export const activeSameConditionMarket = (comparables: MarketAnalysisComparable[]) => activeMarketCompetitors(comparables)
  .filter((comparable) => comparable.marketContextGroup === "same_condition");

export const activeOtherConditionAlternatives = (comparables: MarketAnalysisComparable[]) => activeMarketCompetitors(comparables)
  .filter((comparable) => comparable.marketContextGroup !== "same_condition");

export const replaceActiveAutomaticComparables = (
  existing: MarketAnalysisComparable[],
  replacements: MarketAnalysisComparable[]
) => {
  const uniqueAutomatic = [...new Map(
    replacements.map((comparable) => [comparable.sourceHash || comparable.id, comparable])
  ).values()];
  const primary = uniqueAutomatic.filter((comparable) => comparable.marketRole !== "competition").slice(0, MARKET_COMPARABLE_MAX);
  const competition = uniqueAutomatic.filter((comparable) => comparable.marketRole === "competition");
  const manual = existing.filter((comparable) => !isAutomaticComparable(comparable));
  return [...primary, ...competition, ...manual].map((comparable, sortOrder) => ({ ...comparable, sortOrder }));
};
